import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';

import {
  submitOrbiFeedback,
  submitOrbiFeedbackFailure,
  submitOrbiFeedbackSuccess,
} from '@patient-ui/patient-web/store';
import { ORBI_TOAST_SCOPE } from '@patient-ui/shared/constants';
import {
  ORBI_NEGATIVE_FEEDBACK_REASON_LABELS,
  OrbiFeedbackType,
  OrbiNegativeFeedbackReason,
} from '@patient-ui/shared/enums';
import { OrbiFeedbackRequest } from '@patient-ui/shared/models';
import { ToastService } from '@patient-ui/shared-ui/utils';

import {
  LayoutService,
  OrbiFeedbackSheetRequest,
} from '../../../shared/services/layout.service';

/**
 * Positive / negative feedback bottom sheet (PCSA-13171).
 *
 * Per Figma (AI Agent Canvas) the feedback forms are a bottom sheet docked to
 * the panel bottom — not inline under the response. The sheet is opened by the
 * per-response feedback row via `LayoutService.openOrbiFeedbackSheet(...)`, which
 * carries the target response + sentiment. The response stays visible above.
 *
 * - Positive: an OPTIONAL comment. Submit is always allowed.
 * - Negative: >=1 issue category AND a required "Tell us more" explanation.
 *   Validation is field-level ("Select at least one issue" / "An explanation is
 *   required") shown only after a submit attempt.
 *
 * On submit the feedback is dispatched (`submitOrbiFeedback`) and the sheet waits
 * for the API's answer: on SUCCESS it closes and shows the green success toast
 * ("Your feedback was submitted!"); on FAILURE it stays open with the entries
 * intact and shows an inline error, so Submit is a real retry (PCSA-13473). It
 * used to close and toast success the moment it dispatched, which meant a failed
 * submission — QA reproduced it with DevTools throttled to Offline — still told
 * the user their feedback had been submitted. Success is a toast and failure is
 * inline for the same reason the copy control splits them that way: the failure
 * belongs next to the control that can be tried again.
 */
@Component({
  selector: 'patient-ui-orbi-feedback-sheet',
  templateUrl: './orbi-feedback-sheet.component.html',
  styleUrls: ['./orbi-feedback-sheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrbiFeedbackSheetComponent implements OnInit, OnDestroy {
  /** Negative-feedback categories as [value, label] pairs for the checkboxes. */
  readonly reasons: { value: OrbiNegativeFeedbackReason; label: string }[] =
    Object.values(OrbiNegativeFeedbackReason).map((value) => ({
      value,
      label: ORBI_NEGATIVE_FEEDBACK_REASON_LABELS[value],
    }));

  /** The Other category needs a "(Please explain.)" hint per Figma. */
  readonly OtherReason = OrbiNegativeFeedbackReason.Other;

  /** The active open request (response + sentiment), or null when closed. */
  request: OrbiFeedbackSheetRequest | null = null;

  /** Validation is only surfaced after the user attempts to submit. */
  submitAttempted = false;

  /**
   * Whether the submission dispatched from this sheet is still in flight
   * (PCSA-13473). Holds the sheet open — and Submit disabled — until the API says
   * which way it went, so a second press cannot queue a duplicate.
   */
  submitting = false;

  /**
   * Whether that submission came back FAILED. The sheet stays open and keeps
   * every entry, so the user retries by pressing Submit again rather than
   * re-selecting categories and retyping their explanation.
   */
  submitFailed = false;

  /**
   * Positive form: optional comment. Wrapped in a FormGroup so the `<form>` can
   * bind `[formGroup]` — that makes `(ngSubmit)` Angular's directive output,
   * which calls preventDefault; a bare `<form>` would trigger a native submit
   * (full page reload) on the `type="submit"` button.
   */
  readonly positiveForm = this.fb.nonNullable.group({
    comment: [''],
  });

  /** Negative form: >=1 category + required comment. */
  readonly negativeForm = this.fb.nonNullable.group({
    reasons: this.fb.nonNullable.control<OrbiNegativeFeedbackReason[]>([]),
    comment: ['', [Validators.required]],
  });

  /**
   * The response id whose submission outcome this sheet is waiting on, or null.
   * Feedback submissions run CONCURRENTLY (the effect uses `mergeMap`), so the
   * outcome has to be correlated — an answer about another response must not
   * close this sheet or toast over it.
   */
  private pendingMessageId: string | null = null;

  private readonly destroyed = new Subject<void>();

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly layoutService: LayoutService,
    private readonly toastService: ToastService,
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.layoutService
      .getOrbiFeedbackSheet()
      .pipe(takeUntil(this.destroyed))
      .subscribe((request) => {
        this.request = request;
        // Reset the forms each time the sheet opens for a fresh response. The
        // pending/failed state is dropped with them: whatever the sheet was
        // waiting on belongs to the response the user just left, and letting that
        // outcome land would close the newly opened sheet. The abandoned
        // submission still latches its own row on success (the store keeps
        // feedback per messageId).
        if (request) {
          this.submitAttempted = false;
          this.submitting = false;
          this.submitFailed = false;
          this.pendingMessageId = null;
          this.positiveForm.reset({ comment: '' });
          this.negativeForm.reset({ reasons: [], comment: '' });
        }
        this.cdr.markForCheck();
      });

    // The API's answer — not the dispatch — decides what the user is told
    // (PCSA-13473).
    this.actions$
      .pipe(
        ofType(submitOrbiFeedbackSuccess, submitOrbiFeedbackFailure),
        takeUntil(this.destroyed)
      )
      .subscribe((action) => {
        if (action.messageId !== this.pendingMessageId) {
          return;
        }
        this.pendingMessageId = null;
        this.submitting = false;
        if (action.type === submitOrbiFeedbackSuccess.type) {
          this.onSubmitSucceeded();
        } else {
          this.submitFailed = true;
        }
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  get isOpen(): boolean {
    return this.request !== null;
  }

  get isPositive(): boolean {
    return this.request?.sentiment === OrbiFeedbackType.Positive;
  }

  get title(): string {
    return this.isPositive ? 'Positive Feedback' : 'Negative Feedback';
  }

  // --- Negative validation state (shown only after a submit attempt) --------
  get noReasonSelected(): boolean {
    return this.negativeForm.controls.reasons.value.length === 0;
  }

  get commentMissing(): boolean {
    return this.negativeForm.controls.comment.value.trim().length === 0;
  }

  get showReasonError(): boolean {
    return this.submitAttempted && this.noReasonSelected;
  }

  get showCommentError(): boolean {
    return this.submitAttempted && this.commentMissing;
  }

  // --- Negative category checkboxes ----------------------------------------
  isReasonSelected(value: OrbiNegativeFeedbackReason): boolean {
    return this.negativeForm.controls.reasons.value.includes(value);
  }

  toggleReason(value: OrbiNegativeFeedbackReason): void {
    const current = this.negativeForm.controls.reasons.value;
    const next = current.includes(value)
      ? current.filter((r) => r !== value)
      : [...current, value];
    this.negativeForm.controls.reasons.setValue(next);
  }

  // --- Close / submit -------------------------------------------------------
  close(): void {
    this.layoutService.closeOrbiFeedbackSheet();
  }

  submit(): void {
    // A press while the previous one is still in flight is ignored rather than
    // sent: the button is disabled for it, but Enter on a focused form would
    // otherwise submit the same feedback twice.
    if (this.submitting) {
      return;
    }
    if (this.isPositive) {
      this.submitPositive();
    } else {
      this.submitNegative();
    }
  }

  /** Label on the Submit pill — reflects the in-flight state (PCSA-13473). */
  get submitLabel(): string {
    return this.submitting ? 'Submitting…' : 'Submit';
  }

  private submitPositive(): void {
    const messageId = this.request?.message.messageId;
    if (!messageId) {
      return;
    }
    const comment = this.positiveForm.controls.comment.value.trim();
    this.dispatchFeedback(messageId, {
      messageId,
      feedback: OrbiFeedbackType.Positive,
      comment: comment.length > 0 ? comment : undefined,
    });
  }

  private submitNegative(): void {
    const messageId = this.request?.message.messageId;
    if (!messageId) {
      return;
    }
    this.submitAttempted = true;
    // Negative feedback requires >=1 category AND a comment (AC validation).
    if (this.noReasonSelected || this.commentMissing) {
      return;
    }
    this.dispatchFeedback(messageId, {
      messageId,
      feedback: OrbiFeedbackType.Negative,
      feedbackReason: this.negativeForm.controls.reasons.value,
      comment: this.negativeForm.controls.comment.value.trim(),
    });
  }

  /**
   * Arm the pending state, then dispatch.
   *
   * The order matters: the client-composed "How does this work?" explainer is
   * answered by the effect WITHOUT a call (PCSA-13159 AC 6 — it carries a local
   * id the gateway never issued), so its success action arrives synchronously
   * inside this `dispatch`. Arming afterwards would miss that outcome and leave
   * the sheet stuck on "Submitting…".
   */
  private dispatchFeedback(
    messageId: string,
    feedback: OrbiFeedbackRequest
  ): void {
    this.pendingMessageId = messageId;
    this.submitting = true;
    this.submitFailed = false;
    this.store.dispatch(submitOrbiFeedback({ messageId, feedback }));
  }

  /**
   * Close the sheet + show the green success toast (Figma). Reached only from a
   * `submitOrbiFeedbackSuccess` for THIS sheet's submission — never from the
   * dispatch itself (PCSA-13473).
   */
  private onSubmitSucceeded(): void {
    this.close();
    this.toastService.show('Your feedback was submitted!', {
      classname: 'border-success-dark',
      icon: 'check_circle',
      scope: ORBI_TOAST_SCOPE,
    });
  }
}
